import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { NewPositionDialog } from "@/components/NewPositionDialog";
import { ClosePositionDialog } from "@/components/ClosePositionDialog";
import CoinIcon from "@/components/CoinIcon";
import { LiveHedgeStatus } from "@/components/LiveHedgeStatus";
import KillSwitchButton from "@/components/KillSwitchButton";
import DailyLossLimitBanner from "@/components/DailyLossLimitBanner";
import EditPositionModal from "@/components/EditPositionModal";
import { fmtSignedUsd, fmtUsd, fmtPct, colorForPnl, shortDate } from "@/lib/format";
import ExchangePositions from "@/components/ExchangePositions";

export default function Positions() {
  const [tab, setTab] = useState("OPEN");
  const [closingPos, setClosingPos] = useState(null);
  const [deletingPos, setDeletingPos] = useState(null);
  const [editingPos, setEditingPos] = useState(null);
  const [editMode, setEditMode] = useState("tpsl");
  const [expanded, setExpanded] = useState({});
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const { data: prices } = useQuery({
    queryKey: ["prices", "ETHUSDT"],
    queryFn: () => api.prices("ETHUSDT"),
    refetchInterval: 5000,
  });
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: api.getSettings,
  });
  const { data: positions, isLoading } = useQuery({
    queryKey: ["positions", tab],
    queryFn: () => api.positions({ status: tab, limit: 200 }),
    refetchInterval: 12000,
  });

  const delMut = useMutation({
    mutationFn: api.deletePosition,
    onSuccess: () => {
      toast.success("Silindi");
      qc.invalidateQueries({ queryKey: ["positions"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    },
    onError: (e) => toast.error(errMsg(e, "Silinemedi")),
  });

  const totals = useMemo(() => {
    if (!positions) return null;
    return positions.reduce(
      (acc, p) => ({
        net: acc.net + p.net_pnl,
        bonus: acc.bonus + p.bonus_credit,
        fees: acc.fees + p.total_fees,
      }),
      { net: 0, bonus: 0, fees: 0 }
    );
  }, [positions]);

  return (
    <div className="p-5 space-y-4">
      {/* 🛑 Günlük Zarar Kesici durumu (limit varsa görünür) */}
      <DailyLossLimitBanner />

      {/* Borsa API'den canlı pozisyonlar (Darkex + Binance) */}
      <ExchangePositions />

      <div className="flex items-center justify-between">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[#121212] border border-[#27272A] rounded-sm h-9 p-0.5">
            <TabsTrigger
              value="OPEN"
              data-testid="tab-open"
              className="rounded-sm data-[state=active]:bg-[#27272A] data-[state=active]:text-white text-zinc-400 px-4 h-8 text-xs uppercase tracking-wider"
            >
              Açık
            </TabsTrigger>
            <TabsTrigger
              value="CLOSED"
              data-testid="tab-closed"
              className="rounded-sm data-[state=active]:bg-[#27272A] data-[state=active]:text-white text-zinc-400 px-4 h-8 text-xs uppercase tracking-wider"
            >
              Kapatılmış
            </TabsTrigger>
          </TabsList>
          <TabsContent value="OPEN" />
          <TabsContent value="CLOSED" />
        </Tabs>
        <div className="flex items-center gap-2">
          <KillSwitchButton
            openCount={tab === "OPEN" ? (positions?.length || 0) : 0}
            isAdmin={isAdmin}
          />
          <NewPositionDialog defaultPrices={prices} defaults={settings} />
        </div>
      </div>

      {totals && (
        <div className="grid grid-cols-3 gap-2">
          <Summary
            label={tab === "OPEN" ? "Şu Ana Kadar Ücret" : "Toplam Ücretler"}
            value={fmtUsd(totals.fees)}
            color="text-zinc-300"
          />
          <Summary
            label="Bonus Kredisi"
            value={fmtUsd(totals.bonus)}
            color="text-[#ff5d15]"
          />
          <Summary
            label={tab === "OPEN" ? "Açık Net (girişten)" : "Gerçekleşen Net"}
            value={fmtSignedUsd(totals.net)}
            color={colorForPnl(totals.net)}
          />
        </div>
      )}

      <div className="bg-[#121212] border border-[#27272A] rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0F0F0F] text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <Th />
              <Th>{tab === "OPEN" ? "Açılış" : "Kapanış"}</Th>
              <Th>Sembol</Th>
              <Th>Karşı</Th>
              <Th align="right">Miktar</Th>
              <Th align="right">Darkex Giriş/Çıkış</Th>
              <Th align="right">Karşı Giriş/Çıkış</Th>
              <Th align="right">Brüt</Th>
              <Th align="right">Bonus</Th>
              <Th align="right">Ücret</Th>
              <Th align="right">Net</Th>
              <Th align="right">Aksiyon</Th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td className="p-6 text-center text-zinc-500" colSpan={12}>
                  Yükleniyor…
                </td>
              </tr>
            )}
            {!isLoading && positions?.length === 0 && (
              <tr>
                <td className="p-8 text-center text-zinc-500" colSpan={12}>
                  {tab === "OPEN" ? "Açık" : "Kapatılmış"} pozisyon yok.
                </td>
              </tr>
            )}
            {positions?.map((p) => (
              <PositionRow
                key={p.id}
                p={p}
                expanded={!!expanded[p.id]}
                onToggle={() =>
                  setExpanded((s) => ({ ...s, [p.id]: !s[p.id] }))
                }
                onClose={() => setClosingPos(p)}
                onDelete={() => setDeletingPos(p)}
                onEdit={(m) => {
                  setEditMode(m);
                  setEditingPos(p);
                }}
              />
            ))}
          </tbody>
        </table>
      </div>

      <ClosePositionDialog
        open={!!closingPos}
        onOpenChange={(v) => !v && setClosingPos(null)}
        position={closingPos}
        livePrices={prices}
      />

      {editingPos && (
        <EditPositionModal
          position={editingPos}
          mode={editMode}
          onClose={() => setEditingPos(null)}
        />
      )}

      {/* Delete confirm — "SİL" yaz, accidental click korumalı */}
      {deletingPos && (
        <div
          data-testid="delete-confirm-overlay"
          onClick={() => setDeletingPos(null)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)",
            zIndex: 100, display: "flex", alignItems: "center",
            justifyContent: "center", padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#0a0a0b", border: "1px solid rgba(251,113,133,0.3)",
              maxWidth: 420, width: "100%", padding: 24,
            }}
          >
            <div style={{
              fontSize: 11, letterSpacing: "0.25em", textTransform: "uppercase",
              color: "#fb7185", marginBottom: 12,
            }}>Pozisyonu Sil</div>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
              {deletingPos.symbol} — {shortDate(deletingPos.closed_at || deletingPos.opened_at)}
            </h3>
            <p style={{ color: "#9ca3af", fontSize: 13, lineHeight: 1.55,
                        marginBottom: 16 }}>
              Bu kayıt audit/istatistikten kalıcı silinecek (sadece DB kaydı; exchange'de zaten kapalı).
              Devam etmek için aşağıya <b style={{ color: "#fff" }}>SİL</b> yaz.
            </p>
            <DeleteConfirmInput
              onConfirm={() => {
                delMut.mutate(deletingPos.id);
                setDeletingPos(null);
              }}
              onCancel={() => setDeletingPos(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function DeleteConfirmInput({ onConfirm, onCancel }) {
  const [txt, setTxt] = useState("");
  const ok = txt.trim().toUpperCase() === "SİL" || txt.trim().toUpperCase() === "SIL";
  return (
    <>
      <input
        autoFocus
        data-testid="delete-confirm-input"
        type="text"
        value={txt}
        onChange={(e) => setTxt(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && ok) onConfirm();
          if (e.key === "Escape") onCancel();
        }}
        placeholder="SİL yazın"
        style={{
          width: "100%", height: 38, background: "transparent",
          border: "1px solid rgba(255,255,255,0.1)", color: "#fff",
          padding: "0 12px", fontSize: 13, marginBottom: 12,
        }}
      />
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          onClick={onConfirm}
          disabled={!ok}
          data-testid="delete-confirm-btn"
          style={{
            flex: 1, height: 38, background: ok ? "#fb7185" : "rgba(255,255,255,0.05)",
            color: ok ? "#0a0a0b" : "#6b7280", border: 0, fontWeight: 600,
            fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase",
            cursor: ok ? "pointer" : "not-allowed",
          }}
        >
          Sil (Enter)
        </button>
        <button
          type="button"
          onClick={onCancel}
          style={{
            flex: 1, height: 38, background: "transparent",
            color: "#9ca3af", border: "1px solid rgba(255,255,255,0.1)",
            fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase",
            cursor: "pointer",
          }}
        >
          İptal (Esc)
        </button>
      </div>
    </>
  );
}

const PositionRow = ({ p, expanded, onToggle, onClose, onDelete }) => (
  <>
    <tr
      data-testid={`position-row-${p.id}`}
      className="border-t border-[#1A1A1A] hover:bg-[#161616]"
    >
      <Td>
        <button
          onClick={onToggle}
          className="text-zinc-500 hover:text-white"
          data-testid={`expand-${p.id}`}
        >
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
      </Td>
      <Td className="text-zinc-400">
        {shortDate(p.status === "OPEN" ? p.opened_at : p.closed_at)}
      </Td>
      <Td>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
          <CoinIcon symbol={p.symbol} size="sm" />
          <span className="font-mono text-xs text-white">{p.symbol}</span>
          <button
            type="button"
            onClick={p.status === "OPEN" && onEdit
              ? () => onEdit("leverage") : undefined}
            data-testid={`lev-badge-${p.id}`}
            title={p.status === "OPEN"
              ? `Kaldıraç: ${p.leverage}x — değiştirmek için tıkla`
              : `Kaldıraç: ${p.leverage}x`}
            style={{
              fontSize: 9, fontWeight: 700, letterSpacing: "0.05em",
              padding: "1px 5px",
              background: "rgba(255, 93, 21, 0.12)",
              color: "#ff5d15",
              border: "1px solid rgba(255, 93, 21, 0.3)",
              borderRadius: 2,
              cursor: p.status === "OPEN" && onEdit ? "pointer" : "default",
            }}
          >
            {p.leverage}x
          </button>
          {p.status === "OPEN" && onEdit && (
            <button
              type="button"
              onClick={() => onEdit("tpsl")}
              data-testid={`tpsl-btn-${p.id}`}
              title="TP / SL düzenle"
              style={{
                fontSize: 9, fontWeight: 600, letterSpacing: "0.05em",
                padding: "1px 5px",
                background: "transparent",
                color: "#9ca3af",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 2,
                cursor: "pointer",
              }}
            >
              TP/SL
            </button>
          )}
        </span>
      </Td>
      <Td>
        <span className="text-[10px] uppercase tracking-wider bg-[#27272A] px-1.5 py-0.5 rounded-sm">
          {p.counter_exchange}
        </span>
      </Td>
      <Td align="right" mono>{p.size_eth}</Td>
      <Td align="right" mono className="text-[#ff5662]">
        ${p.entry_price_darkex.toFixed(2)}
        {p.exit_price_darkex != null && (
          <>
            <span className="text-zinc-600 mx-1">/</span>
            <span className="text-zinc-300">${p.exit_price_darkex.toFixed(2)}</span>
          </>
        )}
      </Td>
      <Td align="right" mono className="text-[#2bd98a]">
        ${p.entry_price_counter.toFixed(2)}
        {p.exit_price_counter != null && (
          <>
            <span className="text-zinc-600 mx-1">/</span>
            <span className="text-zinc-300">${p.exit_price_counter.toFixed(2)}</span>
          </>
        )}
      </Td>
      <Td align="right" mono className={colorForPnl(p.gross_pnl)}>
        {fmtSignedUsd(p.gross_pnl)}
      </Td>
      <Td align="right" mono className="text-[#ff5d15]">
        {fmtUsd(p.bonus_credit)}
      </Td>
      <Td align="right" mono className="text-zinc-400">
        {fmtUsd(p.total_fees)}
      </Td>
      <Td
        align="right"
        mono
        className={`font-semibold ${colorForPnl(p.net_pnl)}`}
      >
        {p.status === "CLOSED" ? fmtSignedUsd(p.net_pnl) : "—"}
      </Td>
      <Td align="right">
        {p.status === "OPEN" ? (
          <Button
            data-testid={`close-btn-${p.id}`}
            size="sm"
            onClick={onClose}
            className="h-7 px-2 bg-[#2bd98a] hover:bg-[#28A745] text-black rounded-sm font-semibold text-xs"
          >
            Kapat
          </Button>
        ) : (
          <button
            data-testid={`delete-btn-${p.id}`}
            onClick={onDelete}
            className="text-zinc-500 hover:text-[#ff5662] p-1"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </Td>
    </tr>
    {expanded && (
      <tr className="border-t border-[#1A1A1A] bg-[#0E0E0E]">
        <td colSpan={12} className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
            <Detail label="Kaldıraç" value={`${p.leverage}x`} />
            <Detail label="Bonus Oranı" value={fmtPct(p.bonus_rate * 100, 1)} />
            <Detail label="Darkex Komisyon" value={fmtPct(p.darkex_fee_rate * 100, 3)} />
            <Detail label="Karşı Komisyon" value={fmtPct(p.counter_fee_rate * 100, 3)} />
            <Detail label="Darkex K/Z (short)" value={fmtSignedUsd(p.darkex_pnl)} color={colorForPnl(p.darkex_pnl)} />
            <Detail label="Karşı K/Z (long)" value={fmtSignedUsd(p.counter_pnl)} color={colorForPnl(p.counter_pnl)} />
            <Detail label="Funding Ücreti" value={fmtUsd(p.funding_fees)} />
            <Detail label="Ek Ücretler" value={fmtUsd(p.extra_fees)} />
            {p.notes && (
              <div className="col-span-full">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Notlar</div>
                <div className="text-zinc-300 whitespace-pre-line">{p.notes}</div>
              </div>
            )}
          </div>
          {p.status === "OPEN" && p.live_execution && (
            <div className="mt-3 border border-white/[0.08]">
              <LiveHedgeStatus position={p} />
            </div>
          )}
        </td>
      </tr>
    )}
  </>
);

const Th = ({ children, align = "left" }) => (
  <th className={`px-3 py-2 font-medium text-${align}`}>{children}</th>
);
const Td = ({ children, align = "left", mono, className = "" }) => (
  <td className={`px-3 py-2 text-${align} ${mono ? "font-mono" : ""} ${className}`}>
    {children}
  </td>
);

const Summary = ({ label, value, color }) => (
  <div className="bg-[#121212] border border-[#27272A] rounded-md p-3">
    <div className="text-[10px] uppercase tracking-wider text-zinc-500">
      {label}
    </div>
    <div className={`text-xl font-mono font-semibold mt-0.5 ${color}`}>
      {value}
    </div>
  </div>
);

const Detail = ({ label, value, color = "text-white" }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-zinc-500">
      {label}
    </div>
    <div className={`font-mono ${color}`}>{value}</div>
  </div>
);
